import { replace } from 'lodash';

import { getEnv, RestHandler, getCsrfToken } from '@ctct/ctct-helpers';

import { getAccountData, getCustomerAudit } from './DistUI';
import axios from '../../../axiosConfig';
import AddOnIds from '../constants/addOnIds';

const getBaseUrl = () => {
  if (getEnv() === 'local') {
    return 'https://localhost:9000/v1/myaccountapi';
  }

  return `https://app${getEnv()}constantcontact.com/v1/myaccountapi`;
};

const getStripeBaseUrl = () => {
  return `https://app${getEnv()}constantcontact.com/v1/stripe`;
};

const options = { headers: {} };
const token = getCsrfToken();

if (getEnv() !== 'local') {
  options.headers['X-CSRF-Token'] = token;
}

const restHandler = new RestHandler(getBaseUrl(), options);
const DEFAULT_OPTIONS = {
  mode: 'cors',
  credentials: 'include',
  cache: 'no-store',
  redirect: 'follow',
  headers: {
    'X-CSRF-Token': token,
    'X-Requested-With': 'XMLHttpRequest',
    'content-type': 'application/json',
  },
};

const downloadFile = (fileName) => {
  return window.fetch(`${getBaseUrl()}/billing/customers/users/nonprofit_download?filename=${fileName}`, {
    method: 'GET',
    ...DEFAULT_OPTIONS,
  });
};

const getAgreementToken = async (paypalToken) => {
  const response = await fetch(`${getBaseUrl()}/billing/customers/users/paypalagreement`, {
    method: 'PUT',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify({
      token: paypalToken,
    }),
  });

  if (response.ok) {
    const result = await response.json();

    return result;
  }

  const err = await response.json();

  throw err;
};

const getAvailableCurrencies = async () =>
  (await axios.get(`${getBaseUrl()}/billing/customers/availablecurrency`, DEFAULT_OPTIONS)).data;

const getBillingInfo = async () =>
  (await axios.get(`${getBaseUrl()}/billing/customers/users/billinginfo`, DEFAULT_OPTIONS)).data;

const getCountries = async () => (await axios.get(`${getBaseUrl()}/geo/countries`, DEFAULT_OPTIONS)).data;

const getPaymentPlan = async () =>
  (await axios.get(`${getBaseUrl()}/billing/customers/paymentplan`, DEFAULT_OPTIONS)).data;

const getCancelReasons = async () => (await axios.get(`${getBaseUrl()}/billing/cancelreasons`, DEFAULT_OPTIONS)).data;

const getBillingAccount = async () =>
  (await axios.get(`${getBaseUrl()}/billing/customers/account`, DEFAULT_OPTIONS)).data;

const getCustomer = async () => (await axios.get(`${getBaseUrl()}/billing/customers`, DEFAULT_OPTIONS)).data;

const getStripeScheduledDowngrade = async () => {
  try {
    const response = await axios.get(`${getStripeBaseUrl()}/scheduled-downgrade`, DEFAULT_OPTIONS);

    return response.data;
  } catch (error) {
    // 404 means no scheduled downgrade
    if (error?.response?.status === 404) {
      return null;
    }

    throw error;
  }
};

const cancelStripeScheduledDowngrade = async () => {
  await axios.delete(`${getStripeBaseUrl()}/scheduled-downgrade`, DEFAULT_OPTIONS);
};

const getPlanRating = async (params) => {
  const accountData = await getAccountData();
  const {
    contactUsed: used,
    currentPlanId,
    customer,
    discount: { discountDescription: description, discountPercent } = {},
    discountCode,
    getActivePromotionCodes,
    newAddOnServices,
    paramPlanGroupId,
    paymentPlanGroupId: paymentPlanId,
    selectedCurrency: currencyCode,
    newPlanType,
    selectedPlan: { id: newPlanId } = {},
    paymentPeriod,
    smsUsed,
    existingAddOnServices,
    isStripeAccount = false,
  } = params;
  const { planDetail: { planGroupId } = {} } = accountData;

  const usageData = [{ usageType: 'CONTACTS', used: replace(used, /,|\+/g, ''), resetValue: 0, minValue: null }];

  // const usageData = [{ usageType: 'CONTACTS', used, resetValue: 0, minValue: null }];
  if (smsUsed != null && newAddOnServices && newAddOnServices.some((addon) => addon === AddOnIds.CTCT_SMS_ADDON)) {
    usageData.push({ usageType: 'SMS', used: replace(smsUsed, /,/g, ''), resetValue: 0, minValue: null });
  }

  let includeTax = false;

  if (customer) {
    const { city, country, address1, zipcode, state } = customer;

    if (
      city?.length &&
      country?.length &&
      address1?.length &&
      zipcode?.length &&
      ((country !== 'MX' && country !== 'CA' && country !== 'US') || state?.length)
    ) {
      includeTax = true;
    }
  }

  if (isStripeAccount) {
    includeTax = true;
  }

  const postBody = {
    includeTax,
    customer,
    currencyCode,
    planGroupId: paramPlanGroupId || planGroupId,
    currentPlanId,
    newPlanType,
    newPlanId,
    newAddOnServices,
    paymentPeriod: paymentPeriod || 0,
    paymentPlanId,
    usageData,
    existingAddOnServices: existingAddOnServices || undefined,
    stripeAccount: isStripeAccount,
  };

  if (getActivePromotionCodes) {
    postBody.activePromotionCodes = discountCode ? [discountCode] : [];
  } else {
    postBody.newPromotionCodes = discountCode ? [discountCode] : [];
  }

  try {
    const newToken = getCsrfToken();
    const response = await fetch(`${getBaseUrl()}/billing/customers/plan/rating`, {
      method: 'POST',
      ...DEFAULT_OPTIONS,
      headers: {
        ...DEFAULT_OPTIONS.headers,
        'X-CSRF-Token': newToken,
      },
      body: JSON.stringify(postBody),
    });
    const rating = await response.json();

    return {
      ...rating,
      description,
      name: 'paymentPlan',
      discountPercent,
    };
  } catch (err) {
    return err;
  }
};

const getPaymentPlanRatings = async ({
  selectedPlan,
  contactUsed,
  selectedCurrency,
  currentPlanId,
  customer,
  allAddOns,
  smsUsed,
  paymentPlan: iPaymentPlan,
  isStripeAccount = false,
}) => {
  try {
    if (selectedPlan) {
      const paymentPlan = iPaymentPlan ? { ...iPaymentPlan } : await getPaymentPlan();

      if (paymentPlan.planDiscounts.length >= 1) {
        const planRatings = await Promise.all(
          paymentPlan.planDiscounts.map((discount) =>
            getPlanRating({
              selectedPlan,
              selectedCurrency,
              contactUsed,
              discount,
              paymentPlanGroupId: paymentPlan.paymentPlanGroupId,
              currentPlanId,
              customer,
              smsUsed,
              newAddOnServices: allAddOns.filter((ao) => ao.checked).map((ao) => ao.id),
              isStripeAccount,
            })
          )
        );

        return {
          planRatings,
          currentPaymentPeriod: paymentPlan.currentPaymentPeriod,
          paymentPlanGroupId: paymentPlan.paymentPlanGroupId,
          skipThisStep: paymentPlan.planDiscounts.length === 1,
        };
      }

      return {
        planRatings: [],
        currentPaymentPeriod: paymentPlan.currentPaymentPeriod,
        paymentPlanGroupId: paymentPlan.paymentPlanGroupId,
        skipThisStep: true,
      };
    }

    return Promise.resolve({
      planRatings: [],
      currentPaymentPeriod: 0,
    });
  } catch (err) {
    console.error(err);
    throw err;
  }
};

const getDesignRating = async (params) => {
  const { selectedCurrency: currencyCode } = params;

  const postBody = {
    currencyCode,
    orderLines: [{ itemId: 'CTCT-CAMPAIGN-DESIGN-RATE-1' }],
  };

  try {
    const response = await fetch(`${getBaseUrl()}/billing/customers/profservices/rating`, {
      method: 'POST',
      ...DEFAULT_OPTIONS,
      body: JSON.stringify(postBody),
    });
    const rating = await response.json();

    return {
      ...rating,
      name: 'designRate',
    };
  } catch (err) {
    return err;
  }
};

const getPaypalToken = async (returnUrl) => {
  const url = returnUrl || `https://app${getEnv()}constantcontact.com/pages/myaccount/base/purchase/checkout`;
  const response = await fetch(`${getBaseUrl()}/billing/customers/users/paypaltoken`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify({
      returnURL: url,
      cancelURL: url,
    }),
  });

  if (response.ok) {
    const result = await response.json();

    return result;
  }

  const err = await response.json();

  throw err;
};

const getRecommendedUpgradePlan = async ({ feature, activity, usage, countryCode }) => {
  let result = null;

  try {
    let url = `/plan/customers/recommendedupgradeplan/featurecode/${feature}/activitycode/${activity}`;

    if (usage) {
      url += `?usage=${usage}`;
    }

    if (countryCode) url += `${usage ? '&' : '?'}country=${countryCode}`;

    result = (await axios.get(`${getBaseUrl()}${url}`, DEFAULT_OPTIONS)).data;
  } catch (e) {
    //
  }

  return result;
};

const getAddOns = async (currency, paymentPlanId) => {
  const {
    planDetail: { planGroupId },
  } = await getAccountData();

  try {
    const addOns = await restHandler.get(
      `/plan/customers/addons/plangroups/${planGroupId}/plans/${paymentPlanId}?currencyCode=${currency}`
    );

    const addOnsOrdering = [
      'CTCT-SMS-ADDON',
      'CTCT-MARKETING-ASSISTANT-102',
      'CTCT-INBOX-PREVIEW-101',
      'CTCT-SEO-101',
      'CTCT-ARCHIVE-101',
    ];

    const sortedAddOns =
      addOns == null
        ? null
        : addOns.sort((a, b) => {
            const indexA = addOnsOrdering.findIndex((ao) => ao === a.id);
            const indexB = addOnsOrdering.findIndex((ao) => ao === b.id);

            return indexA - indexB;
          });

    return sortedAddOns;
  } catch (err) {
    return [];
  }
};

const getPlanGroupWithGroup = async (currency, count = 0, usage, planGroupId) => {
  const audit = await getCustomerAudit();
  let response;

  if (audit) {
    const { createdOn } = audit;
    const currentTime = new Date().getTime();

    // More than 5 minutes ago account created.
    if (currentTime - createdOn > 60000 * 5) {
      response = await fetch(
        `${getBaseUrl()}/plan/customers/users/currency/${currency}/plan_group?count=${count}&planGroupId=${planGroupId}`,
        {
          ...DEFAULT_OPTIONS,
          method: 'GET',
        }
      );
    } else {
      response = await fetch(
        `${getBaseUrl()}/plan/customers/users/currency/${currency}/plan_group?count=${count}&usage=${usage}&plan_group_id=${planGroupId}`,
        {
          ...DEFAULT_OPTIONS,
          method: 'GET',
        }
      );
    }

    const result = await response.json();

    if (response.ok) {
      return result;
    }

    throw result;
  }

  throw new Error('DIST UI error');
};

const getPlanGroup = async (currency, count = 0, usage) => {
  const audit = await getCustomerAudit();
  let response;

  if (audit) {
    const { createdOn } = audit;
    const currentTime = new Date().getTime();

    // More than 5 minutes ago account created.
    if (currentTime - createdOn > 60000 * 5) {
      response = await fetch(`${getBaseUrl()}/plan/customers/users/currency/${currency}/plan_group?count=${count}`, {
        ...DEFAULT_OPTIONS,
        method: 'GET',
      });
    } else {
      response = await fetch(
        `${getBaseUrl()}/plan/customers/users/currency/${currency}/plan_group?count=${count}&usage=${usage}`,
        {
          ...DEFAULT_OPTIONS,
          method: 'GET',
        }
      );
    }

    const result = await response.json();

    if (response.ok) {
      return result;
    }

    throw result;
  }

  throw new Error('DIST UI error');
};

const getStatuses = () => restHandler.get('/billing/customers/users/nonprofit_upload');

const onlineCancel = (data) => {
  return fetch(`${getBaseUrl()}/billing/customers/online_cancel?updateBy=customer`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify(data),
  });
};

const onlineCancelVoid = (data) => {
  return fetch(`${getBaseUrl()}/billing/customers/online_cancel/void`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify(data),
  });
};

const payForPlan = (data) => {
  const newToken = getCsrfToken();

  return fetch(`${getBaseUrl()}/billing/customers/users/plan/pay`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    headers: {
      ...DEFAULT_OPTIONS.headers,
      'X-CSRF-Token': newToken,
    },
    body: JSON.stringify(data),
  });
};

const createAmazonPaySignature = async (data) => {
  const response = await fetch(`${getBaseUrl()}/billing/customers/amazonpay/signature`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify({ payload: data }),
  });

  if (response.ok) {
    const text = await response.text();

    return text;
  }

  const err = await response.json();

  throw err;
};

const getAmazonPayCheckoutSession = async (data) => {
  const response = await fetch(`${getBaseUrl()}/billing/customers/amazonpay/checkoutSession/${data}`, {
    method: 'GET',
    ...DEFAULT_OPTIONS,
  });

  if (response.ok) {
    const text = await response.json();

    return text;
  }

  const err = await response.json();

  throw err;
};

const updateAmazonPayCheckoutSession = async (data) => {
  const response = await fetch(
    `${getBaseUrl()}/billing/customers/amazonpay/checkoutSession/${data.checkoutSessionId}`,
    {
      method: 'PUT',
      ...DEFAULT_OPTIONS,
      body: JSON.stringify({ ...data.checkoutSession }),
    }
  );

  if (response.ok) {
    const text = await response.json();

    return text;
  }

  const err = await response.json();

  throw err;
};

const updateCurrency = (currencyCd) => {
  return fetch(`${getBaseUrl()}/billing/customers/currency`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: currencyCd,
  });
};

const updatePaymentPlan = (paymentPlan) => {
  return fetch(`${getBaseUrl()}/billing/customers/paymentplan`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify(paymentPlan),
  });
};

const uploadFile = (formData) => {
  const { headers, ...headerlessDefaultOptions } = DEFAULT_OPTIONS;

  delete headers['content-type'];

  return window.fetch(`${getBaseUrl()}/billing/customers/users/nonprofit_upload`, {
    method: 'POST',
    ...headerlessDefaultOptions,
    headers,
    body: formData,
  });
};

const getRMCValue = (key) => restHandler.get(`/billing/utils/${key}/configurationvalue`);

const getOfferCodes = async () => {
  const response = await fetch(`${getBaseUrl()}/offers/customers/offercodes`, {
    method: 'GET',
    ...DEFAULT_OPTIONS,
  });

  if (response.ok) {
    const text = await response.json();

    return text;
  }

  const err = await response.json();

  throw err;
};

const getSalesforceAccountData = () => restHandler.get('/billing/customers/salesforce');

const setPendingSuspend = (suspendReason) => {
  return fetch(`${getBaseUrl()}/billing/customers/setpendingsuspend`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: suspendReason,
  });
};

const getCustomerInfo = ({ customerInfo, customerId, currency }) => {
  const {
    first_name: firstName,
    last_name: lastName,
    company_name: organizationName,
    email: contactEmail,
    city,
    address1,
    address2,
    phone: contactPhone,
    state,
    country,
    postal_code: zipcode,
    vatNumber,
    billingPeriod,
  } = customerInfo;

  return {
    customerId,
    userUuid: null,
    firstname: firstName,
    lastname: lastName,
    companyname: organizationName,
    email: contactEmail,
    phone: contactPhone,
    address1,
    address2,
    city,
    state,
    country,
    zipcode,
    contactEmail,
    vatNumber,
    billingType: {
      name: 'R',
    },
    customerInSingleBilling: false,
    billingLetterOption: 'Y',
    currencyCd: currency,
    billingPeriod,
    billingDayOfMonth: 0,
    addressValidationRequired: true,
    noPrepayAutoRenewOptin: 0,
    addressProvided: true,
    singleBilledAccount: false,
    partnerBilledAccount: false,
  };
};

export default {
  downloadFile,
  getAddOns,
  getAgreementToken,
  getAvailableCurrencies,
  getBillingAccount,
  getBillingInfo,
  getCountries,
  getCustomer,
  getDesignRating,
  getPaymentPlan,
  getPaymentPlanRatings,
  getPaypalToken,
  getPlanGroup,
  getPlanGroupWithGroup,
  getPlanRating,
  getStatuses,
  payForPlan,
  getCancelReasons,
  createAmazonPaySignature,
  getAmazonPayCheckoutSession,
  updateAmazonPayCheckoutSession,
  updateCurrency,
  updatePaymentPlan,
  uploadFile,
  onlineCancel,
  onlineCancelVoid,
  getRMCValue,
  getOfferCodes,
  getSalesforceAccountData,
  setPendingSuspend,
  getRecommendedUpgradePlan,
  getCustomerInfo,
  getStripeScheduledDowngrade,
  cancelStripeScheduledDowngrade,
};
